import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

export default class extends Controller {
  static targets = ["query", "status", "results", "empty"]

  static values = {
    searchUrl: String,
    refreshStatesUrl: String,
    refreshCitiesUrl: String,
    statesFrame: String,
    citiesFrame: String,
    minLength: { type: Number, default: 3 }
  }

  connect() {
    this.prefillHandler = () => this.prefillFromOrderForm()
    this.element.addEventListener("shown.bs.modal", this.prefillHandler)
  }

  disconnect() {
    this.element.removeEventListener("shown.bs.modal", this.prefillHandler)
    clearTimeout(this.searchTimeout)
    this.abortController?.abort()
  }

  async search(event) {
    event?.preventDefault()
    clearTimeout(this.searchTimeout)

    const query = this.queryTarget.value.trim()

    if (!query) {
      this.renderEmpty("Informe o nome ou CPF/CNPJ do cliente.")
      return
    }

    if (!this.hasMinimumLength(query)) {
      this.renderMinimumLengthMessage()
      return
    }

    this.showStatus("Buscando pedidos anteriores...", true)
    this.renderSkeleton()
    this.hideEmpty()

    const url = new URL(this.searchUrlValue, window.location.origin)
    url.searchParams.set("query", query)

    this.abortController?.abort()
    this.abortController = new AbortController()

    try {
      const response = await fetch(url, {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
        signal: this.abortController.signal
      })

      if (!response.ok) throw new Error("Erro ao buscar pedidos")

      const data = await response.json()
      const orders = data.orders || []
      const limit = data.limit || orders.length

      this.clearStatus()

      if (!orders.length) {
        this.renderEmpty("Nenhum pedido anterior encontrado.")
        return
      }

    this.resultsTarget.innerHTML = orders.map((order, index) => this.resultTemplate(order, index)).join("")
    this.resultOrders = orders
    this.activeIndex = -1

    if (data.has_more) {
      this.showStatus(`Exibindo os ${limit} resultados mais recentes. Refine a pesquisa para encontrar outros pedidos.`)
      }
    } catch (error) {
      if (error.name === "AbortError") return

      console.error("Erro ao buscar pedidos anteriores:", error)
      this.renderEmpty("Não foi possível buscar pedidos anteriores.")
      this.clearStatus()
    }
  }

  prefillFromOrderForm() {
    const currentName = document.getElementById("order_customer_name")?.value || ""
    const currentTaxId = document.getElementById("order_customer_federal_tax_id")?.value || ""
    const query = currentTaxId || currentName

    this.clearStatus()
    this.hideEmpty()
    this.resultsTarget.innerHTML = ""
    this.resultOrders = []

    if (this.hasQueryTarget) this.queryTarget.value = query
    if (this.hasQueryTarget) {
      requestAnimationFrame(() => this.queryTarget.focus())
    }
    if (this.hasMinimumLength(query)) this.search()
  }

  navigate(event) {
    if (!["ArrowDown", "ArrowUp", "Enter"].includes(event.key)) return
    if (!this.resultOrders?.length) return

    if (event.key === "Enter" && this.activeIndex >= 0) {
      event.preventDefault()
      this.selectResult(this.activeIndex)
      return
    }

    if (event.key === "ArrowDown") {
      event.preventDefault()
      this.activateResult((this.activeIndex + 1) % this.resultOrders.length)
      return
    }

    if (event.key === "ArrowUp") {
      event.preventDefault()
      const nextIndex = this.activeIndex <= 0 ? this.resultOrders.length - 1 : this.activeIndex - 1
      this.activateResult(nextIndex)
    }
  }

  scheduleSearch() {
    clearTimeout(this.searchTimeout)

    const query = this.queryTarget.value.trim()
    if (!query) {
      this.clearStatus()
      this.hideEmpty()
      this.resultsTarget.innerHTML = ""
      this.resultOrders = []
      return
    }

    if (!this.hasMinimumLength(query)) {
      this.renderMinimumLengthMessage()
      return
    }

    this.searchTimeout = setTimeout(() => this.search(), 350)
  }

  hasMinimumLength(query) {
    const digits = query.replace(/\D/g, "")
    const comparable = digits || query
    return comparable.length >= this.minLengthValue
  }

  renderMinimumLengthMessage() {
    clearTimeout(this.searchTimeout)
    this.abortController?.abort()
    this.clearStatus()
    this.resultsTarget.innerHTML = ""
    this.resultOrders = []
    this.renderEmpty(`Digite pelo menos ${this.minLengthValue} caracteres para pesquisar.`)
  }

  async select(event) {
    event.preventDefault()

    const index = Number(event.currentTarget.dataset.orderIndex)
    await this.selectResult(index)
  }

  async selectResult(index) {
    const order = this.resultOrders?.[index]
    if (!order) return

    this.fillValue("order_customer_name", order.customer_name)
    this.fillValue("order_customer_phone", order.customer_phone)
    this.fillValue("order_customer_email", order.customer_email)
    this.fillValue("order_customer_federal_tax_id", order.customer_federal_tax_id)
    this.fillValue("order_zip", order.zip, false)
    this.fillValue("order_address", order.address)
    this.fillValue("order_number", order.number)
    this.fillValue("order_complement", order.complement)
    this.fillValue("order_neighborhood", order.neighborhood)
    this.fillValue("order_latitude", order.latitude)
    this.fillValue("order_longitude", order.longitude)

    await this.fillLocation(order)

    const modalElement = document.getElementById("customerHistoryModal")
    const modal = modalElement ? bootstrap.Modal.getOrCreateInstance(modalElement) : null
    modal?.hide()
  }

  activateResult(index) {
    this.activeIndex = index

    this.resultsTarget.querySelectorAll(".list-group-item[data-order-index]").forEach((item) => {
      const isActive = Number(item.dataset.orderIndex) === index
      item.classList.toggle("active", isActive)
      item.setAttribute("aria-selected", isActive ? "true" : "false")
      item.querySelector("[data-order-link]")?.classList.toggle("text-white", isActive)
      if (isActive) item.scrollIntoView({ block: "nearest" })
    })
  }

  async fillLocation(order) {
    const country = document.getElementById("order_country_id")
    const state = document.getElementById("order_state_id")
    const city = document.getElementById("order_city_id")

    if (country && order.country_id) {
      country.value = order.country_id

      if (this.hasRefreshStatesUrlValue && this.hasStatesFrameValue) {
        await this.visitFrame(`${this.refreshStatesUrlValue}${encodeURIComponent(order.country_id)}`, this.statesFrameValue)
      }
    }

    const refreshedState = document.getElementById("order_state_id") || state
    if (refreshedState && order.state_id) {
      refreshedState.value = order.state_id

      if (this.hasRefreshCitiesUrlValue && this.hasCitiesFrameValue) {
        await this.visitFrame(`${this.refreshCitiesUrlValue}${encodeURIComponent(order.state_id)}`, this.citiesFrameValue)
      }
    }

    const refreshedCity = document.getElementById("order_city_id") || city
    if (refreshedCity && order.city_id) {
      refreshedCity.value = order.city_id
    }

    this.fillValue("order_city", order.city)
    this.fillValue("order_state", order.state)
    this.fillValue("order_country", order.country)
  }

  fillValue(id, value, dispatchEvents = true) {
    const input = document.getElementById(id)
    if (!input) return

    input.value = value || ""
    if (!dispatchEvents) return

    input.dispatchEvent(new Event("input", { bubbles: true }))
    input.dispatchEvent(new Event("change", { bubbles: true }))
  }

  visitFrame(url, frameId) {
    const frame = document.getElementById(frameId)
    if (!frame) {
      Turbo.visit(url, { frame: frameId })
      return Promise.resolve()
    }

    return new Promise((resolve) => {
      frame.addEventListener("turbo:frame-load", () => resolve(), { once: true })
      Turbo.visit(url, { frame: frameId })
    })
  }

  resultTemplate(order, index) {
    const title = this.escapeHtml(order.customer_name || "Cliente sem nome")
    const document = this.escapeHtml(order.customer_federal_tax_id || "Sem CPF/CNPJ")
    const displayId = this.escapeHtml(order.display_id || `#${order.id}`)
    const showUrl = this.escapeHtml(order.show_url || `/orders/${order.id}`)
    const address = this.escapeHtml([
      order.address,
      order.number,
      order.neighborhood,
      order.city,
      order.state
    ].filter(Boolean).join(", "))

    return `
      <div class="list-group-item list-group-item-action" data-order-index="${index}" aria-selected="false">
        <div class="d-flex justify-content-between gap-3">
          <button type="button" class="btn btn-link text-start text-decoration-none text-body p-0 flex-grow-1" data-action="customer-history#select" data-order-index="${index}">
            <div class="fw-semibold">${title}</div>
            <div class="small text-muted">${document}</div>
            <div class="small">${address || "Endereço não informado"}</div>
          </button>
          <div class="text-end text-nowrap small text-muted">
            <a href="${showUrl}" target="_blank" rel="noopener" class="text-decoration-none" data-order-link title="Abrir pedido em nova aba" aria-label="Abrir pedido ${displayId} em nova aba">
              <i class="bi bi-box-arrow-up-right"></i>
              ${displayId}
            </a>
            <div>${this.escapeHtml(order.created_at || "")}</div>
          </div>
        </div>
      </div>
    `
  }

  renderSkeleton() {
    this.resultOrders = []
    this.resultsTarget.innerHTML = Array.from({ length: 4 }, () => `
      <div class="list-group-item">
        <div class="d-flex justify-content-between gap-3">
          <div class="flex-grow-1">
            <div class="sh-cell mb-2" style="width: 45%;"></div>
            <div class="sh-cell mb-2" style="width: 30%;"></div>
            <div class="sh-cell" style="width: 80%;"></div>
          </div>
          <div style="width: 84px;">
            <div class="sh-cell mb-2"></div>
            <div class="sh-cell"></div>
          </div>
        </div>
      </div>
    `).join("")
  }

  showStatus(message, withSpinner = false) {
    if (!this.hasStatusTarget) return

    const spinner = withSpinner
      ? '<span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>'
      : ""

    this.statusTarget.innerHTML = `${spinner}${this.escapeHtml(message)}`
  }

  clearStatus() {
    if (this.hasStatusTarget) this.statusTarget.textContent = ""
  }

  renderEmpty(message) {
    if (!this.hasEmptyTarget) return

    this.emptyTarget.textContent = message
    this.emptyTarget.classList.remove("d-none")
    this.resultsTarget.innerHTML = ""
  }

  hideEmpty() {
    if (this.hasEmptyTarget) this.emptyTarget.classList.add("d-none")
  }

  escapeHtml(value) {
    return String(value || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;")
  }
}
